import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Alert,
  ListGroup,
  Tabs,
  Tab,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import OperationProgress from '../components/OperationProgress';
import { useCopyProgress } from '../hooks/useCopyProgress';
import EditListField from '../components/EditListField';
import MissingSelectOption from '../components/MissingSelectOption';
import {
  validateCustomerEquipmentForm,
  validateField,
  ValidationRules,
  hasErrors,
  formatErrors,
} from '../utils/validation';

function emptyForm() {
  return {
    account_no: '',
    customer_name: '',
    equipment_name: '',
    make: '',
    model: '',
    power: '',
    serial_no: '',
    reg_no: '',
    fleet_no: '',
    cat: '',
    purch_date: '',
    additional_info: '',
    year_of_manufacture: '',
    service_name: '',
    next_serv_date: '',
    next_serv_meter: '',
    next_serv_notes: '',
    site_name: '',
    inspection_test: '',
  };
}

function normalizeOptionalInt(value) {
  if (value === '' || value == null) {
    return null;
  }
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function appendCopySuffix(value, maxLength) {
  const suffix = ' (Copy)';
  const base = String(value || '').trim() || 'Record';
  if (base.length + suffix.length <= maxLength) {
    return `${base}${suffix}`;
  }
  return `${base.slice(0, maxLength - suffix.length)}${suffix}`;
}

function buildCopyPayload(record) {
  const payload = buildSavePayload(
    toForm({
      ...record,
      equipment_name: appendCopySuffix(record.equipment_name, 120),
    }),
    record.account_no || null,
    null,
    []
  );
  if (record.cust_id != null) {
    payload.cust_id = record.cust_id;
  }
  return payload;
}

function buildSavePayload(formData, accountNo, customerName, customers) {
  let custId = null;
  if (accountNo || customerName) {
    const matchedCustomer = customers.find(
      (customer) =>
        (accountNo && customer.account_no && customer.account_no === accountNo) ||
        (customerName && customer.name?.trim().toLowerCase() === customerName.trim().toLowerCase())
    );
    custId = matchedCustomer?.id ?? null;
  }

  return {
    cust_id: custId,
    account_no: accountNo,
    equipment_name: formData.equipment_name?.trim(),
    make: formData.make?.trim() || null,
    model: formData.model?.trim() || null,
    power: formData.power?.trim() || null,
    serial_no: formData.serial_no?.trim() || null,
    reg_no: formData.reg_no?.trim() || null,
    fleet_no: formData.fleet_no?.trim() || null,
    cat: formData.cat?.trim() || null,
    purch_date: formData.purch_date || null,
    additional_info: formData.additional_info?.trim() || null,
    year_of_manufacture: normalizeOptionalInt(formData.year_of_manufacture),
    service_name: formData.service_name?.trim() || null,
    next_serv_date: formData.next_serv_date || null,
    next_serv_meter: normalizeOptionalInt(formData.next_serv_meter),
    next_serv_notes: formData.next_serv_notes?.trim() || null,
    site_name: formData.site_name?.trim() || null,
    inspection_test: formData.inspection_test?.trim() || null,
  };
}

function toForm(record) {
  if (!record) {
    return emptyForm();
  }
  return {
    account_no: record.account_no || '',
    customer_name: record.customer_name || '',
    equipment_name: record.equipment_name || '',
    make: record.make || '',
    model: record.model || '',
    power: record.power || '',
    serial_no: record.serial_no || '',
    reg_no: record.reg_no || '',
    fleet_no: record.fleet_no || '',
    cat: record.cat || '',
    purch_date: record.purch_date ? String(record.purch_date).split('T')[0] : '',
    additional_info: record.additional_info || '',
    year_of_manufacture: record.year_of_manufacture ?? '',
    service_name: record.service_name || '',
    next_serv_date: record.next_serv_date ? String(record.next_serv_date).split('T')[0] : '',
    next_serv_meter: record.next_serv_meter ?? '',
    next_serv_notes: record.next_serv_notes || '',
    site_name: record.site_name || '',
    inspection_test: record.inspection_test || '',
  };
}

function EditableTextCell({
  row,
  field,
  editingCell,
  editingValue,
  savingCell,
  onStartEdit,
  onChange,
  onSave,
  onKeyDown,
  maxLength,
  emphasize = false,
}) {
  const cellKey = `${row.id}-${field}`;
  const isEditing = editingCell === cellKey;
  const isSaving = savingCell === cellKey;
  const displayValue = row[field] || '—';

  if (isEditing) {
    return (
      <Form.Control
        type="text"
        size="sm"
        value={editingValue}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => onSave(row, field)}
        onKeyDown={(e) => onKeyDown(e, row, field)}
        autoFocus
        disabled={isSaving}
        style={{ minWidth: '120px' }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      className={emphasize ? 'fw-semibold' : undefined}
      style={{ cursor: 'pointer', display: 'inline-block', minWidth: '48px' }}
      onClick={() => onStartEdit(row, field)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(row, field);
        }
      }}
      title="Click to edit"
    >
      {displayValue}
    </span>
  );
}

function CustomerEquipmentModal({ record, onClose, onSave }) {
  const [formData, setFormData] = useState(emptyForm());
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerMatches, setShowCustomerMatches] = useState(false);
  const [sites, setSites] = useState([]);
  const [inspectionNames, setInspectionNames] = useState([]);
  const [hireCategories, setHireCategories] = useState([]);
  const [saveError, setSaveError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [customersRes, inspectionRes, categoriesRes] = await Promise.all([
          axios.get('/api/customers', { params: { page: 1, limit: 10000 } }),
          axios.get('/api/inspection-tests/inspection-names'),
          axios.get('/api/hire-categories/codes'),
        ]);
        setCustomers(customersRes.data.customers || []);
        setInspectionNames(inspectionRes.data || []);
        setHireCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      } catch (error) {
        console.error('Failed to fetch lookup data:', error);
      }
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    const nextForm = toForm(record);
    setFormData(nextForm);
    setCustomerSearch(nextForm.customer_name);
    setSaveError('');
    setValidationErrors({});
    setShowCustomerMatches(false);
  }, [record]);

  useEffect(() => {
    const account = formData.account_no?.trim();
    if (!account) {
      setSites([]);
      return undefined;
    }

    let active = true;
    const fetchSites = async () => {
      try {
        const response = await axios.get('/api/customer-sites', {
          params: { customer: account, page: 1, limit: 10000 },
        });
        if (active) {
          setSites(response.data.customerSites || []);
        }
      } catch {
        if (active) {
          setSites([]);
        }
      }
    };

    fetchSites();
    return () => {
      active = false;
    };
  }, [formData.account_no]);

  const filteredCustomers = useMemo(() => {
    const query = customerSearch.trim().toLowerCase();
    if (!query) {
      return [];
    }
    return customers
      .filter((customer) => {
        const name = (customer.name || '').toLowerCase();
        const account = (customer.account_no || '').toLowerCase();
        return name.includes(query) || account.includes(query);
      })
      .slice(0, 20);
  }, [customers, customerSearch]);

  const handleSelectCustomer = (customer) => {
    setFormData((prev) => ({
      ...prev,
      customer_name: customer.name || '',
      account_no: customer.account_no || '',
      site_name: '',
    }));
    setCustomerSearch(customer.name || '');
    setShowCustomerMatches(false);
    setSaveError('');
  };

  const handleCustomerSearchChange = (value) => {
    setCustomerSearch(value);
    setShowCustomerMatches(true);
    setFormData((prev) => ({
      ...prev,
      customer_name: value,
      account_no: '',
      site_name: '',
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setSaveError('');
  };

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setSaveError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateCustomerEquipmentForm(formData);
    if (hasErrors(errors)) {
      setValidationErrors(errors);
      setSaveError(formatErrors(errors));
      return;
    }

    let accountNo = formData.account_no?.trim() || null;
    let customerName = formData.customer_name?.trim() || null;

    if (customerSearch.trim()) {
      const matchedCustomer = customers.find(
        (customer) =>
          (accountNo && customer.account_no && customer.account_no === accountNo) ||
          customer.name?.trim().toLowerCase() === customerSearch.trim().toLowerCase()
      );
      if (matchedCustomer) {
        accountNo = matchedCustomer.account_no || null;
        customerName = matchedCustomer.name;
      }
    }

    setValidationErrors({});
    setSaveError('');
    try {
      await onSave(buildSavePayload(formData, accountNo, customerName, customers));
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to save customer equipment';
      setSaveError(message);
    }
  };

  return (
    <Modal
      show={true}
      onHide={onClose}
      size="xl"
      centered
      enforceFocus={false}
      restoreFocus={false}
      style={{ zIndex: 1060 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>{record ? 'Edit Customer Equipment' : 'Add Customer Equipment'}</Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body>
          {saveError && (
            <Alert variant="danger" className="mb-3">
              {saveError}
            </Alert>
          )}
          <Tabs defaultActiveKey="identity" className="mb-3">
            <Tab eventKey="identity" title="Identity">
              <Container fluid>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Equipment Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="equipment_name"
                        value={formData.equipment_name}
                        onChange={handleChange}
                        maxLength={120}
                        isInvalid={!!validationErrors.equipment_name}
                      />
                      {validationErrors.equipment_name && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.equipment_name}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <EditListField
                      inputName="make"
                      label="Make"
                      active
                      refreshKey={record?.id ?? 'new'}
                      valuesUrl="/api/customer-equipment/distinct-values?field=make"
                      value={formData.make}
                      onChange={(make) => handleFieldChange('make', make)}
                      maxLength={30}
                    />

                    <EditListField
                      inputName="model"
                      label="Model"
                      active
                      refreshKey={record?.id ?? 'new'}
                      valuesUrl="/api/customer-equipment/distinct-values?field=model"
                      value={formData.model}
                      onChange={(model) => handleFieldChange('model', model)}
                      maxLength={128}
                    />

                    <EditListField
                      inputName="power"
                      label="Power"
                      active
                      refreshKey={record?.id ?? 'new'}
                      valuesUrl="/api/customer-equipment/distinct-values?field=power"
                      value={formData.power}
                      onChange={(power) => handleFieldChange('power', power)}
                      maxLength={60}
                    />

                    <Form.Group className="mb-3">
                      <Form.Label>Category</Form.Label>
                      <Form.Select
                        name="cat"
                        value={formData.cat}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.cat}
                      >
                        <option value="">Select category...</option>
                        <MissingSelectOption
                          value={formData.cat}
                          options={hireCategories}
                          getOptionValue={(category) => category.code}
                        />
                        {hireCategories.map((category) => (
                          <option key={category.code} value={category.code}>
                            {category.code} — {category.name}
                          </option>
                        ))}
                      </Form.Select>
                      {validationErrors.cat && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.cat}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3 position-relative">
                      <Form.Label>Customer</Form.Label>
                      <Form.Control
                        type="search"
                        placeholder="Search by customer name or account..."
                        value={customerSearch}
                        onChange={(e) => handleCustomerSearchChange(e.target.value)}
                        onFocus={() => setShowCustomerMatches(true)}
                        onBlur={() => {
                          window.setTimeout(() => setShowCustomerMatches(false), 150);
                        }}
                        autoComplete="off"
                      />
                      {showCustomerMatches &&
                        customerSearch.trim() &&
                        filteredCustomers.length > 0 && (
                          <ListGroup
                            className="position-absolute w-100 shadow-sm"
                            style={{ zIndex: 2, maxHeight: '220px', overflowY: 'auto' }}
                          >
                            {filteredCustomers.map((customer) => (
                              <ListGroup.Item
                                key={customer.id || `${customer.account_no}-${customer.name}`}
                                action
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleSelectCustomer(customer)}
                              >
                                <div className="fw-medium">{customer.name}</div>
                                <div className="small text-muted">
                                  {customer.account_no
                                    ? `Account: ${customer.account_no}`
                                    : 'Cash customer'}
                                </div>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Account No</Form.Label>
                      <Form.Control
                        name="account_no"
                        value={formData.account_no || ''}
                        onChange={handleChange}
                        maxLength={20}
                        placeholder="Selected from customer search or enter manually"
                        isInvalid={!!validationErrors.account_no}
                      />
                      {validationErrors.account_no && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.account_no}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Site Name</Form.Label>
                      <Form.Select
                        name="site_name"
                        value={formData.site_name}
                        onChange={handleChange}
                        disabled={!formData.account_no}
                        isInvalid={!!validationErrors.site_name}
                      >
                        <option value="">
                          {formData.account_no
                            ? 'Select site (optional)...'
                            : 'Select a customer first'}
                        </option>
                        <MissingSelectOption
                          value={formData.site_name}
                          options={sites}
                          getOptionValue={(site) => site.site_name}
                        />
                        {sites.map((site) => (
                          <option key={site.id} value={site.site_name}>
                            {site.site_name}
                          </option>
                        ))}
                      </Form.Select>
                      {validationErrors.site_name && (
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                          {validationErrors.site_name}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Inspection Test</Form.Label>
                      <Form.Select
                        name="inspection_test"
                        value={formData.inspection_test}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.inspection_test}
                      >
                        <option value="">Select an inspection...</option>
                        <MissingSelectOption
                          value={formData.inspection_test}
                          options={inspectionNames.map((name) => ({ name }))}
                          getOptionValue={(item) => item.name}
                        />
                        {inspectionNames.map((name, index) => (
                          <option key={index} value={name}>
                            {name}
                          </option>
                        ))}
                      </Form.Select>
                      {validationErrors.inspection_test && (
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                          {validationErrors.inspection_test}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Container>
            </Tab>

            <Tab eventKey="details" title="Details">
              <Container fluid>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Serial No</Form.Label>
                      <Form.Control
                        type="text"
                        name="serial_no"
                        value={formData.serial_no}
                        onChange={handleChange}
                        maxLength={50}
                        isInvalid={!!validationErrors.serial_no}
                      />
                      {validationErrors.serial_no && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.serial_no}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Reg No</Form.Label>
                      <Form.Control
                        type="text"
                        name="reg_no"
                        value={formData.reg_no}
                        onChange={handleChange}
                        maxLength={30}
                        isInvalid={!!validationErrors.reg_no}
                      />
                      {validationErrors.reg_no && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.reg_no}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fleet No</Form.Label>
                      <Form.Control
                        type="text"
                        name="fleet_no"
                        value={formData.fleet_no}
                        onChange={handleChange}
                        maxLength={20}
                        isInvalid={!!validationErrors.fleet_no}
                      />
                      {validationErrors.fleet_no && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.fleet_no}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Purchase Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="purch_date"
                        value={formData.purch_date}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.purch_date}
                      />
                      {validationErrors.purch_date && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.purch_date}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Year of Manufacture</Form.Label>
                      <Form.Control
                        type="number"
                        step="1"
                        min="1900"
                        max="2100"
                        name="year_of_manufacture"
                        value={formData.year_of_manufacture}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.year_of_manufacture}
                      />
                      {validationErrors.year_of_manufacture && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.year_of_manufacture}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Service Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="service_name"
                        value={formData.service_name}
                        onChange={handleChange}
                        maxLength={60}
                        isInvalid={!!validationErrors.service_name}
                      />
                      {validationErrors.service_name && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.service_name}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Next Service Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="next_serv_date"
                        value={formData.next_serv_date}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.next_serv_date}
                      />
                      {validationErrors.next_serv_date && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.next_serv_date}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Next Service Meter</Form.Label>
                      <Form.Control
                        type="number"
                        step="1"
                        min="0"
                        name="next_serv_meter"
                        value={formData.next_serv_meter}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.next_serv_meter}
                      />
                      {validationErrors.next_serv_meter && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.next_serv_meter}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Additional Info</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="additional_info"
                        value={formData.additional_info}
                        onChange={handleChange}
                        maxLength={512}
                        isInvalid={!!validationErrors.additional_info}
                      />
                      {validationErrors.additional_info && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.additional_info}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Next Service Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="next_serv_notes"
                        value={formData.next_serv_notes}
                        onChange={handleChange}
                        maxLength={512}
                        isInvalid={!!validationErrors.next_serv_notes}
                      />
                      {validationErrors.next_serv_notes && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.next_serv_notes}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Container>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {record ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function CustomerEquipment() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const { copyProgress, wrapCopy } = useCopyProgress();
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchRecords = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/customer-equipment', {
        params: { page, limit: pagination.limit, search },
      });
      setRecords(response.data.customerEquipment || []);
      setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      setError(`Failed to fetch customer equipment: ${err.message}`);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecords(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchRecords(newPage, searchTerm);
  };

  const handleSave = async (payload) => {
    try {
      if (editingRecord) {
        await axios.put(`/api/customer-equipment/${editingRecord.id}`, payload);
        setSuccess('Customer equipment updated successfully');
      } else {
        await axios.post('/api/customer-equipment', payload);
        setSuccess('Customer equipment created successfully');
      }
      setShowModal(false);
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      throw err;
    }
  };

  const startTextEdit = (row, field) => {
    setEditingCell(`${row.id}-${field}`);
    setEditingValue(row[field] == null ? '' : String(row[field]));
  };

  const cancelTextEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveTextEdit = async (row, field) => {
    const cellKey = `${row.id}-${field}`;
    if (editingCell !== cellKey) {
      return;
    }

    const raw = editingValue.trim();
    const current = row[field] == null ? '' : String(row[field]).trim();

    if (raw === current) {
      cancelTextEdit();
      return;
    }

    const fieldError = validateField(field, raw, ValidationRules.customerEquipment[field], row);
    if (fieldError) {
      setError(fieldError);
      cancelTextEdit();
      return;
    }

    setSavingCell(cellKey);
    try {
      const response = await axios.put(`/api/customer-equipment/${row.id}`, {
        [field]: raw || null,
      });
      setRecords((prev) =>
        prev.map((record) => (record.id === row.id ? { ...record, ...response.data } : record))
      );
      setSuccess(field === 'equipment_name' ? 'Equipment name updated' : 'Serial number updated');
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update record');
    } finally {
      setSavingCell(null);
      cancelTextEdit();
    }
  };

  const handleTextKeyDown = (e, row, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveTextEdit(row, field);
    } else if (e.key === 'Escape') {
      cancelTextEdit();
    }
  };

  const handleCopy = async (record) => {
    setCopyingId(record.id);
    setError('');
    try {
      await wrapCopy('Copying customer equipment...', 1, async (tick) => {
        await axios.post('/api/customer-equipment', buildCopyPayload(record));
        tick({ success: true });
      });
      setSuccess('Customer equipment copied successfully');
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to copy customer equipment');
    } finally {
      setCopyingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }
    try {
      await axios.delete(`/api/customer-equipment/${id}`);
      setSuccess('Record deleted successfully');
      fetchRecords(pagination.page, searchTerm);
    } catch {
      setError('Failed to delete record');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL customer equipment records?')) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all customer equipment records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete('/api/customer-equipment');
      setSuccess(`All records deleted successfully (${response.data.deletedCount || 0} records)`);
      fetchRecords(1, '');
    } catch {
      setError('Failed to delete all records');
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-tools me-3 text-success" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0">Customer Equipment</h1>
              <p className="text-muted mb-0">Manage customer-owned equipment records</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button
            variant="primary"
            onClick={() => {
              setEditingRecord(null);
              setShowModal(true);
            }}
            className="me-2"
          >
            Add Record
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {copyProgress && (
        <OperationProgress
          title={copyProgress.title}
          processed={copyProgress.processed}
          total={copyProgress.total}
          success={copyProgress.success}
          failed={copyProgress.failed}
          variant="info"
        />
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by equipment name, account, make, model, or serial no..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center p-3">Loading...</div>
          ) : records.length === 0 ? (
            <div className="text-center p-3">No records found.</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover className="mb-3">
                  <thead>
                    <tr>
                      <th>Equipment Name</th>
                      <th>Account No</th>
                      <th>Make</th>
                      <th>Model</th>
                      <th>Serial No</th>
                      <th>Site</th>
                      <th>Inspection</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <EditableTextCell
                            row={row}
                            field="equipment_name"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startTextEdit}
                            onChange={setEditingValue}
                            onSave={saveTextEdit}
                            onKeyDown={handleTextKeyDown}
                            maxLength={120}
                            emphasize
                          />
                        </td>
                        <td>{row.account_no || '-'}</td>
                        <td>{row.make || '-'}</td>
                        <td>{row.model || '-'}</td>
                        <td>
                          <EditableTextCell
                            row={row}
                            field="serial_no"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startTextEdit}
                            onChange={setEditingValue}
                            onSave={saveTextEdit}
                            onKeyDown={handleTextKeyDown}
                            maxLength={50}
                          />
                        </td>
                        <td>{row.site_name || '-'}</td>
                        <td>{row.inspection_test || '-'}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setEditingRecord(row);
                                setShowModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopy(row)}
                              disabled={copyingId === row.id}
                            >
                              {copyingId === row.id ? 'Copying...' : 'Copy'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(row.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <CustomerEquipmentModal
          record={editingRecord}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </Container>
  );
}

export default CustomerEquipment;
